import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Shield, Users, AlertTriangle, ClipboardCheck, Lock, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../components/Button';
import styles from './legal.module.css';

interface PolicyDoc {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
  content: string;
}

const policies: PolicyDoc[] = [
  {
    id: 'acceptable-use',
    title: 'Acceptable Use Policy',
    icon: <ClipboardCheck size={18} />,
    description: 'System access rules, data handling, and prohibited use',
    content: `## 1. Purpose
This policy defines acceptable use of Tasking.tech company systems, networks, devices, and data.

## 2. Scope
Applies to all employees, contractors, consultants, and third parties with access to Tasking.tech systems.

## 3. Acceptable Use

### General
- Use company systems for authorized business purposes
- Protect login credentials — never share passwords or API keys
- Lock your workstation when unattended
- Report security incidents immediately to security@tasking.tech

### Data Handling
- Customer data must never be copied to personal devices
- Do not store credentials in plaintext
- Do not transmit sensitive data over unencrypted channels
- Follow data classification levels (Confidential, Sensitive, Internal, Public)

### Source Code
- All code changes require pull request review before merge
- Never commit secrets, API keys, or credentials to source control
- Follow secure coding practices (OWASP Top 10)

### Infrastructure Access
- Production access limited to authorized personnel
- Use MFA for all production system access
- Access granted on least-privilege basis

## 4. Prohibited Use
- Accessing systems or data beyond authorized scope
- Sharing credentials with unauthorized parties
- Installing unauthorized software on production systems
- Using company systems for illegal activities
- Attempting to bypass security controls
- Cryptocurrency mining on company infrastructure

## 5. Personal Devices
- Screen lock enabled (timeout ≤ 5 minutes)
- Full disk encryption enabled
- Current operating system and security patches

## 6. Enforcement
Violations may result in revocation of access, disciplinary action, or legal action.`,
  },
  {
    id: 'confidentiality',
    title: 'Confidentiality Agreement',
    icon: <Lock size={18} />,
    description: 'NDA for customer and company data protection',
    content: `## 1. Confidential Information Includes
- Customer data, credentials, and PII
- Source code, algorithms, and trade secrets
- Business strategies, financial data, and pricing
- Security architecture and encryption keys
- Internal communications and meeting content
- Unreleased product features and roadmaps

## 2. Obligations
1. **Protect** — Take reasonable measures to prevent unauthorized access
2. **Limit use** — Use only for authorized business purposes
3. **No disclosure** — Never disclose to third parties without authorization
4. **Return/destroy** — Return all materials upon termination
5. **Report breaches** — Immediately report to security@tasking.tech

## 3. Duration
Survives termination of employment for **2 years**.

## 4. Remedies
Breach may result in immediate termination of access, legal action, or injunctive relief.`,
  },
  {
    id: 'security-training',
    title: 'Security Training Program',
    icon: <Users size={18} />,
    description: '7 training modules including AI security, quarterly schedule',
    content: `## Training Modules

### Module 1: Security Fundamentals
- SOC 2 and why it matters
- Data classification levels
- Incident reporting procedures

### Module 2: Authentication & Access
- Password best practices
- Multi-factor authentication
- Principle of least privilege
- Securing API keys and credentials

### Module 3: Phishing & Social Engineering
- Identifying phishing emails
- Verifying sender identity
- Reporting suspicious communications

### Module 4: Secure Development
- OWASP Top 10 overview
- Code review requirements
- Secret scanning in CI/CD
- Input validation and output encoding

### Module 5: Data Handling
- Customer data protection
- Encryption at rest and in transit
- Data retention and deletion policies
- GDPR and CCPA basics

### Module 6: Incident Response
- How to identify a security incident
- Escalation procedures
- Post-incident review process

### Module 7: AI & Agent Security
- Prompt injection awareness
- Credential handling in AI operations
- Tool execution safety
- Output sanitization requirements

## Schedule
| Quarter | Activity |
|---------|----------|
| Q1 | Annual training (all modules) |
| Q2 | Phishing simulation exercise |
| Q3 | Incident response tabletop drill |
| Q4 | Policy review and updates |

New hires must complete all modules within **14 days** before receiving production access.`,
  },
  {
    id: 'incident-response',
    title: 'Incident Response Plan',
    icon: <AlertTriangle size={18} />,
    description: 'Severity levels, 6 response phases, escalation matrix',
    content: `## Severity Levels

| Level | Response Time | Examples |
|-------|--------------|---------|
| **Critical** | 1 hour | Data breach, full outage, credential leak |
| **High** | 4 hours | Auth failure, partial outage, suspicious access |
| **Medium** | 1 business day | Failed security scan, unusual traffic |
| **Low** | 2 business days | Policy question, minor config issue |

## Response Phases

### Phase 1: Detection
Automated monitoring, audit log anomaly detection, error rate alerts, user reports.

### Phase 2: Triage (within 15 minutes)
Assign severity, identify scope, assign incident commander.

### Phase 3: Containment
Isolate affected systems, revoke compromised credentials, enable enhanced logging.

### Phase 4: Eradication
Root cause analysis, patch deployment, configuration correction.

### Phase 5: Recovery
Restore service, monitor for recurrence, validate data integrity.

### Phase 6: Post-Incident (within 72 hours)
Incident report, lessons learned, control updates.

## Escalation

| Severity | Notify | Within |
|----------|--------|--------|
| Critical | Security + Engineering + CEO | Immediately |
| High | Security + Engineering | 1 hour |
| Medium | Assigned engineer | 4 hours |
| Low | Security team (async) | Next business day |

## Communication

| Audience | When |
|----------|------|
| Internal team | Immediately |
| Affected customers | Within 24 hours of confirmed breach |
| Status page | Within 15 minutes of detection |
| Regulators (GDPR) | Within 72 hours |`,
  },
  {
    id: 'tsp-security',
    title: 'TSP Security Controls',
    icon: <Shield size={18} />,
    description: 'Server-side controls enforced for all technology service partners',
    content: `## Controls Enforced for Every TSP Partner

### 1. Encryption
- **TLS 1.3** on all connections
- **AES-256-GCM** for stored credentials
- Per-tenant credential isolation

### 2. Authentication
- OAuth 2.0 + PKCE, SSO (Auth0/Okta), Password + TOTP 2FA
- MFA enforceable per tenant
- JWT sessions with HttpOnly, Secure, SameSite cookies

### 3. RBAC
| Role | Permissions |
|------|------------|
| Owner | Full control |
| Admin | Users, settings, integrations |
| Member | Sessions, tasks, AI features |
| Viewer | Read-only |
| Billing | Billing only |

### 4. Audit Logging
- Every action logged with tenant_id, user_id, IP, severity
- 12-month retention
- Partners can query logs for their tenants only

### 5. Rate Limiting
Per-IP, per-endpoint sliding window on all API routes.

### 6. CSRF Protection
SameSite cookies + Origin/Referer validation on state-changing requests.

### 7. Input Validation
Zod schemas, parameterized SQL, prompt injection guardrails (6 categories).

### 8. DLP (Data Loss Prevention)
Per-tenant/partner policies for content inspection, outbound restrictions, file type controls.

### 9. Tenant Isolation
- Database queries filtered by tenant_id
- Cloudflare Workers V8 isolate sandboxing per request
- R2 storage keyed by user prefix

### 10. SOC 2 Coverage
CC6.0, CC7.0, CC8.0, CC9.0, A1.0, C1.0`,
  },
  {
    id: 'access-review',
    title: 'Access Review Template',
    icon: <FileText size={18} />,
    description: 'Quarterly review of all production system access',
    content: `## Quarterly Access Review

### Systems Reviewed
- Cloudflare Dashboard
- GitHub Repository
- D1 Database (Direct Access)
- Cloudflare Workers Secrets
- Stripe Dashboard
- LiveKit Dashboard
- Domain Registrar
- PyPI (tt-desktop)

### Review Process
1. List all users with access to each system
2. Verify each user still needs their current access level
3. Revoke access for departed users or changed roles
4. Document findings and actions taken
5. Sign off by reviewer and security lead

### Schedule
| Quarter | Due Date |
|---------|----------|
| Q1 | March 31 |
| Q2 | June 30 |
| Q3 | September 30 |
| Q4 | December 31 |

Full templates with signature fields available in docs/policies/access-review-template.md`,
  },
];

export default function PoliciesPage() {
  useEffect(() => {
    const root = document.getElementById('root');
    if (root) { root.style.overflow = 'auto'; root.style.height = 'auto'; }
    document.body.style.overflow = 'auto';
    return () => { if (root) { root.style.overflow = ''; root.style.height = ''; } document.body.style.overflow = ''; };
  }, []);

  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <div className={styles.container}>
      <>
        <title>Security Policies | Tasking.tech</title>
        <meta name="description" content="Tasking.tech security policies — acceptable use, confidentiality, training, incident response, TSP controls" />
      </>

      <nav className={styles.nav}>
        <div className={styles.navContent}>
          <Link to="/" className={styles.logo}>
            <img src="/logo.png" alt="Tasking.tech" className={styles.logoIcon} />
            <span className={styles.logoText}>
              <span>tasking</span>
              <span className={styles.techSuffix}>.tech</span>
            </span>
          </Link>
          <Button variant="ghost" asChild>
            <Link to="/security">
              <ArrowLeft size={16} /> Back to Security
            </Link>
          </Button>
        </div>
      </nav>

      <main className={styles.content}>
        <h1>Security Policies</h1>
        <p className={styles.lastUpdated}>
          These policies govern how Tasking.tech protects your data and operations. All team members and TSP partners must comply.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '2rem' }}>
          {policies.map((policy) => {
            const isExpanded = expandedId === policy.id;
            return (
              <div key={policy.id} style={{
                background: 'var(--surface, #1a2332)',
                border: '1px solid var(--border, #334155)',
                borderRadius: 12,
                overflow: 'hidden',
              }}>
                <button
                  onClick={() => setExpandedId(isExpanded ? null : policy.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    width: '100%',
                    padding: '1rem 1.25rem',
                    background: 'none',
                    border: 'none',
                    color: 'var(--foreground, #f8fafc)',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <span style={{ color: 'var(--primary, #f97316)', flexShrink: 0 }}>{policy.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{policy.title}</div>
                    <div style={{ fontSize: '0.75rem', opacity: 0.6, marginTop: 2 }}>{policy.description}</div>
                  </div>
                  {isExpanded ? <ChevronDown size={16} style={{ opacity: 0.5 }} /> : <ChevronRight size={16} style={{ opacity: 0.5 }} />}
                </button>
                {isExpanded && (
                  <div style={{
                    padding: '0 1.25rem 1.25rem',
                    fontSize: '0.85rem',
                    lineHeight: 1.7,
                    borderTop: '1px solid var(--border, #334155)',
                    paddingTop: '1rem',
                  }}>
                    {policy.content.split('\n').map((line, i) => {
                      if (line.startsWith('## ')) return <h2 key={i} style={{ fontSize: '1.1rem', fontWeight: 700, margin: '1.2rem 0 0.4rem' }}>{line.slice(3)}</h2>;
                      if (line.startsWith('### ')) return <h3 key={i} style={{ fontSize: '0.95rem', fontWeight: 600, margin: '0.8rem 0 0.3rem' }}>{line.slice(4)}</h3>;
                      if (line.startsWith('| ') && line.includes('|')) {
                        const cells = line.split('|').filter(Boolean).map(c => c.trim());
                        if (cells.every(c => /^[-:]+$/.test(c))) return null;
                        return (
                          <div key={i} style={{ display: 'flex', gap: 0 }}>
                            {cells.map((c, j) => (
                              <div key={j} style={{ flex: 1, padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border, #334155)', fontSize: '0.8rem' }}>{c}</div>
                            ))}
                          </div>
                        );
                      }
                      if (line.startsWith('- ')) return <li key={i} style={{ marginLeft: '1.2rem', marginBottom: '0.2rem' }}>{line.slice(2)}</li>;
                      if (line.trim() === '') return <br key={i} />;
                      return <p key={i} style={{ margin: '0.3rem 0' }}>{line}</p>;
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </main>

      <footer className={styles.footer}>
        <div className={styles.footerContent}>
          <div className={styles.footerBrand}>
            <img src="/logo.png" alt="Tasking.tech" className={styles.footerLogo} />
            <span>tasking<span className={styles.techSuffix}>.tech</span></span>
          </div>
          <div className={styles.footerLinks}>
            <Link to="/docs">Docs</Link>
            <Link to="/security">Security</Link>
            <Link to="/policies">Policies</Link>
            <Link to="/privacy-policy">Privacy Policy</Link>
            <Link to="/terms-of-service">Terms of Service</Link>
            <Link to="/">Home</Link>
          </div>
          <span className={styles.copyright}>© 2026 Tasking.tech. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}
